import { IconButton, Stack, Typography } from "@mui/material";
import React from "react";
import HorizontalFill from "@entrilia/shared/components/HorizontalFill.tsx";
import { getSerieType, isLinearSettings } from "../../../../viewer/chart/helper.ts";
import AreaChartIcon from "../../../../common/visualization/icons/AreaChartIcon.tsx";
import BarChartIcon from "../../../../common/visualization/icons/BarChartIcon.tsx";
import LineChartIcon from "../../../../common/visualization/icons/LineChartIcon.tsx";
import { useAppDispatch } from "../../../../../store/hooks/useAppDispatch.ts";
import { fieldsStateActions } from "../../../../../store/thunks/fieldStatesThunks.ts";
import type { ChartStyleSettings, SeriesSettings, SerieType } from "../../../../../../../../../api/types/svTypes.ts";

interface Props {
  settings: ChartStyleSettings;
  serie: SeriesSettings;
  readonlyMode: boolean;
}

export default function SerieTypeItem({ settings, serie, readonlyMode }: Props) {
  const dispatch = useAppDispatch();
  const supported = isLinearSettings(settings);
  const serieType = getSerieType(settings, serie);

  const handleSerieTypeChanged = React.useCallback(
    (value: SerieType) =>
      dispatch(fieldsStateActions.updateSeriesSettings({ name: serie.name, changes: { serieType: value } })),
    [dispatch, serie]
  );

  if (!supported) {
    return <></>;
  }

  return (
    <Stack direction="row" sx={{ alignItems: "center" }}>
      <Typography sx={{ color: "text.secondary" }}>Serie Type</Typography>
      <HorizontalFill />
      <BarSerie selectedType={serieType} disabled={readonlyMode} onSelected={handleSerieTypeChanged} />
      <LineSerie selectedType={serieType} disabled={readonlyMode} onSelected={handleSerieTypeChanged} />
      <AreaSerie selectedType={serieType} disabled={readonlyMode} onSelected={handleSerieTypeChanged} />
    </Stack>
  );
}

interface SerieProps {
  selectedType: SerieType;
  disabled: boolean;
  onSelected: (type: SerieType) => void;
}

function BarSerie({ selectedType, disabled, onSelected }: SerieProps) {
  return (
    <IconButton
      sx={{ borderRadius: "unset", opacity: disabled ? 0.5 : 1 }}
      disabled={disabled}
      onClick={() => onSelected("Bar")}
    >
      <BarChartIcon
        sx={(theme) => ({
          color: selectedType === "Bar" ? theme.palette.primary.main : theme.palette.secondary.light,
        })}
      />
    </IconButton>
  );
}

function LineSerie({ selectedType, disabled, onSelected }: SerieProps) {
  return (
    <IconButton
      sx={{ borderRadius: "unset", opacity: disabled ? 0.5 : 1 }}
      disabled={disabled}
      onClick={() => onSelected("Line")}
    >
      <LineChartIcon
        sx={(theme) => ({
          color: selectedType === "Line" ? theme.palette.primary.main : theme.palette.secondary.light,
        })}
      />
    </IconButton>
  );
}

function AreaSerie({ selectedType, disabled, onSelected }: SerieProps) {
  return (
    <IconButton
      sx={{ borderRadius: "unset", opacity: disabled ? 0.5 : 1 }}
      disabled={disabled}
      onClick={() => onSelected("Area")}
    >
      <AreaChartIcon
        sx={(theme) => ({
          color: selectedType === "Area" ? theme.palette.primary.main : theme.palette.secondary.light,
        })}
      />
    </IconButton>
  );
}
